import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { API_ENDPOINTS } from "../../../ApiService";
import apiService from "../../../ApiService";
import Section from "../section/Section";
import {
  Box,
  Container,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Checkbox,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import styled from "styled-components";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Header from "../Header";
import { tokens } from "../../../theme";

const headerCellStyles = {
  backgroundColor: "#7A3B69",
  color: "#fff",
  fontWeight: "bold",
  textAlign: "right",
  fontSize: "0.9rem",
};

const ClientTable = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  // Define desktop as screens at least "sm" size
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));

  const [clients, setClients] = useState([]);
  // We'll allow filtering by client name in this example
  const [filterNames, setFilterNames] = useState(["All"]);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await apiService.get(API_ENDPOINTS.CLIENTS);
        setClients(response.data);
      } catch (error) {
        console.error("Error fetching clients:", error);
      }
    };
    fetchClients();
  }, []);

  // Create a list of unique client names for the filter
  const uniqueNames = useMemo(() => {
    const names = clients.map((c) => `${c.prenom} ${c.nom}`).filter(Boolean);
    return ["All", ...Array.from(new Set(names)).sort()];
  }, [clients]);

  // Filter clients based on the selected names; if "All" is selected, show everything
  const filteredClients = useMemo(() => {
    if (filterNames.includes("All")) {
      return clients;
    }
    return clients.filter((client) => {
      const fullName = `${client.prenom} ${client.nom}`;
      return filterNames.includes(fullName);
    });
  }, [clients, filterNames]);

  // Desktop view: a table with sticky header
  const renderTable = () => (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight: 800,
        borderRadius: 2,
        overflowX: "auto",
        boxShadow: 3,
      }}
    >
      <Table stickyHeader aria-label="client table">
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellStyles}>Nom</TableCell>
            <TableCell sx={headerCellStyles}>Prénom</TableCell>
            <TableCell sx={headerCellStyles}>Email</TableCell>
            <TableCell sx={headerCellStyles}>Date de Naissance</TableCell>
            <TableCell sx={headerCellStyles}>Age</TableCell>
            <TableCell sx={headerCellStyles}>Poids d'origine</TableCell>
            <TableCell sx={headerCellStyles}>Poids actuel</TableCell>
            <TableCell sx={headerCellStyles}>Taille</TableCell>
            <TableCell sx={headerCellStyles}>Activity Level</TableCell>
            <TableCell sx={headerCellStyles}>Workout/Jour</TableCell>
            <TableCell sx={headerCellStyles}>Dashboard</TableCell>
            <TableCell sx={headerCellStyles}>Bilan</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredClients.map((client) => (
            <TableRow
              key={client.clientID}
              sx={{
                "&:nth-of-type(odd)": { backgroundColor: "#f5f5f5" },
                "&:hover": { backgroundColor: theme.palette.action.hover },
              }}
            >
              <TableCell sx={{ textAlign: "right" }}>{client.nom}</TableCell>
              <TableCell sx={{ textAlign: "right" }}>{client.prenom}</TableCell>
              <TableCell sx={{ textAlign: "right" }}>{client.email}</TableCell>
              <TableCell sx={{ textAlign: "right" }}>
                {client.datedenaissance}
              </TableCell>
              <TableCell sx={{ textAlign: "right" }}>{client.age}</TableCell>
              <TableCell sx={{ textAlign: "right" }}>
                {client.poidsdorigine}
              </TableCell>
              <TableCell sx={{ textAlign: "right" }}>
                {client.poidsactuel}
              </TableCell>
              <TableCell sx={{ textAlign: "right" }}>{client.taille}</TableCell>
              <TableCell sx={{ textAlign: "right" }}>
                {client.activitylevel}
              </TableCell>
              <TableCell sx={{ textAlign: "right" }}>
                {client.nbjourdeworkout}
              </TableCell>
              <TableCell sx={{ textAlign: "right" }}>
                <Link to={`/dashboard/${client.idUser}`}>Dashboard</Link>
              </TableCell>
              <TableCell sx={{ textAlign: "right" }}>
                <Link to={`/bilan/${client.idUser}`}>Bilan</Link>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Mobile view: a simple list
  const renderList = () => (
    <Box>
      {filteredClients.map((client) => (
        <Paper key={client.clientID} sx={{ p: 2, mb: 1 }}>
          <Typography variant="h6">
            {client.prenom} {client.nom}
          </Typography>
          <Typography>Email: {client.email}</Typography>
          <Typography>Age: {client.age}</Typography>
          <Typography>
            <Link to={`/dashboard/${client.idUser}`}>Dashboard</Link> |{" "}
            <Link to={`/bilan/${client.idUser}`}>Bilan</Link>
          </Typography>
        </Paper>
      ))}
    </Box>
  );

  return (
    <Section>
      <Box m="20px">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              m="10px 0"
            >
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={uniqueNames}
                value={filterNames}
                onChange={(event, newValue) => setFilterNames(newValue)}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label="Filter by Client"
                    placeholder="Select client(s)"
                  />
                )}
                sx={{ width: isDesktop ? "400px" : "100%", mb: 2 }}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            {isDesktop ? renderTable() : renderList()}
          </Grid>
        </Grid>
        <Box mt={50} />
      </Box>
    </Section>
  );
};

export default ClientTable;
